// src/admin/AdminManagement.js
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, Outlet, NavLink, useLocation } from 'react-router-dom'; // Import useLocation
import { StudentContext } from '../StudentContext';
import TopNav from '../components/TopNav';
import AddNewStudent from './AddNewStudent';
import './AdminManagement.css';

const originalBlocks = { // Keep for sidebar generation
  'khai-tam': [
    { id: 'khai-tam-1a-nu', name: 'Khai Tâm 1A Nữ' }, { id: 'khai-tam-1b-nam', name: 'Khai Tâm 1B Nam' },
    { id: 'khai-tam-2a-nu', name: 'Khai Tâm 2A Nữ' }, { id: 'khai-tam-2b-nu', name: 'Khai Tâm 2B Nữ' },
    { id: 'khai-tam-2c-nam', name: 'Khai Tâm 2C Nam' }, { id: 'khai-tam-2d-nam', name: 'Khai Tâm 2D Nam' },
  ],
  'ruoc-le': [
    { id: 'ruoc-le-1a-nu', name: 'Rước Lễ 1A Nữ' }, { id: 'ruoc-le-1b-nu', name: 'Rước Lễ 1B Nữ' },
    { id: 'ruoc-le-1c-nam', name: 'Rước Lễ 1C Nam' }, { id: 'ruoc-le-1d-nam', name: 'Rước Lễ 1D Nam' },
    { id: 'ruoc-le-2a-nu', name: 'Rước Lễ 2A Nữ' }, { id: 'ruoc-le-2b-nu', name: 'Rước Lễ 2B Nữ' },
    { id: 'ruoc-le-2c-nam', name: 'Rước Lễ 2C Nam' }, { id: 'ruoc-le-2d-nam', name: 'Rước Lễ 2D Nam' },
  ],
  'them-suc': [
    { id: 'them-suc-1a-nu', name: 'Thêm Sức 1A Nữ' }, { id: 'them-suc-1b-nu', name: 'Thêm Sức 1B Nữ' },
    { id: 'them-suc-1c-nam', name: 'Thêm Sức 1C Nam' }, { id: 'them-suc-1d-nam', name: 'Thêm Sức 1D Nam' },
    { id: 'them-suc-2a-nu', name: 'Thêm Sức 2A Nữ' }, { id: 'them-suc-2b-nu', name: 'Thêm Sức 2B Nữ' },
    { id: 'them-suc-2c-nam', name: 'Thêm Sức 2C Nam' }, { id: 'them-suc-2d-nam', name: 'Thêm Sức 2D Nam' },
  ],
  'bao-dong': [
    { id: 'bao-dong-1a-nu', name: 'Bao Đồng 1A Nữ' }, { id: 'bao-dong-1b-nam', name: 'Bao Đồng 1B Nam' },
    { id: 'bao-dong-2a-nu', name: 'Bao Đồng 2A Nữ' }, { id: 'bao-dong-2b-nam', name: 'Bao Đồng 2B Nam' },
    { id: 'bao-dong-3', name: 'Bao Đồng 3' }, { id: 'bao-dong-4', name: 'Bao Đồng 4' },
    { id: 'du-bi-giao-ly-vien', name: 'Dự Bị Giáo Lý Viên' },
  ],
};

const AdminManagement = () => {
  const navigate = useNavigate();
  const location = useLocation(); // Get current location
  const { studentsData } = useContext(StudentContext);

  const [selectedClass, setSelectedClass] = useState('all');
  const [openBlocks, setOpenBlocks] = useState({});

  useEffect(() => {
    const userRole = localStorage.getItem('userRole') || 'catechist';
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (!isLoggedIn || userRole !== 'admin') {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const toggleBlock = (blockId) => {
    setOpenBlocks((prev) => ({ ...prev, [blockId]: !prev[blockId] }));
  };

  const handleSelectClass = (classId) => {
    setSelectedClass(classId);
  };

  const allStudentsForOutlet = useMemo(() =>
    Object.keys(studentsData || {})
    .flatMap((classId) =>
      (studentsData[classId] || []).map((student) => ({
        ...student,
        classId: classId,
      }))
    )
    .filter((student) => selectedClass === 'all' || student.classId === selectedClass)
  , [studentsData, selectedClass]);

  const renderNavTabs = () => {
    const navLinkClasses = ({ isActive }) =>
      `nav-tab px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out ${isActive ? 'active bg-blue-600 text-white shadow-md' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`;
  
    return (
      <nav className="tabs-nav bg-white p-3 shadow-sm mb-4 admin-container sticky top-0 z-40">
        <ul className="flex flex-wrap space-x-2 sm:space-x-4">
          <li><NavLink to="print-cards" className={navLinkClasses}>In Thẻ</NavLink></li>
          <li><NavLink to="early-attendance" className={navLinkClasses}>Thống kê Đi Lễ</NavLink></li>
          <li><NavLink to="catechist-management" className={navLinkClasses}>Quản lý GLV</NavLink></li>
          <li><NavLink to="add-new-student" className={navLinkClasses}>Thiếu nhi đăng ký mới</NavLink></li>
        </ul>
      </nav>
    );
  };

  const renderSidebar = () => {
    const blockNames = {
      'khai-tam': 'Khai Tâm', 'ruoc-le': 'Rước Lễ',
      'them-suc': 'Thêm Sức', 'bao-dong': 'Bao Đồng',
    };

    return (
      <aside className="sidebar w-64 bg-gray-50 p-4 border-r border-gray-200 overflow-y-auto h-full flex-shrink-0">
        <ul>
          <li key="all-classes" className="mb-1">
            <button
              onClick={() => handleSelectClass('all')}
              className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out block-button ${selectedClass === 'all' ? 'bg-blue-500 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'}`}
            >
              Tất cả các lớp
            </button>
          </li>
          {Object.keys(originalBlocks).map((blockId) => (
            <li key={blockId} className="mb-1">
              <button onClick={() => toggleBlock(blockId)} className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-200 hover:text-gray-800 block-button flex justify-between items-center">
                <span>{blockNames[blockId]}</span>
                <span className={`transform transition-transform duration-200 ${openBlocks[blockId] ? 'rotate-90' : 'rotate-0'}`}>▶</span>
              </button>
              {openBlocks[blockId] && (
                <ul className="submenu pl-4 mt-1 space-y-1">
                  {originalBlocks[blockId].map((classItem) => (
                    <li key={classItem.id}>
                      <button
                        onClick={() => handleSelectClass(classItem.id)}
                        className={`w-full text-left px-3 py-1.5 rounded-md text-xs ${selectedClass === classItem.id ? 'bg-blue-400 text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
                      >
                        {classItem.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </aside>
    );
  };

  // Determine if the sidebar should be shown based on the current path
  // The path for index route (/admin-management) will be /admin-management/print-cards after redirection
  const showSidebar = location.pathname.startsWith('/admin-management/print-cards') ||
                      location.pathname.startsWith('/admin-management/early-attendance');

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <TopNav />
      {renderNavTabs()}
      <div className="flex flex-1 overflow-hidden pt-0">
        {showSidebar && renderSidebar()} {/* Conditionally render sidebar */}
        <main className="flex-1 overflow-y-auto bg-white shadow-inner">
          {/* Pass context to Outlet. All children will receive it.
              Components that don't need it can simply ignore it. */}
          <Outlet context={{ allStudentsForOutlet, selectedClassForOutlet: selectedClass }} />
        </main>
      </div>
    </div>
  );
};

export default AdminManagement;